/**
 * DriverEarnings — driver-only earnings dashboard (mobile-first redesign).
 *
 * UI-only restyle: the backend KPI calculations + /api/driver/kpi response are
 * unchanged. Layout is phone-first and card-based — Today's NET earnings is the
 * hero metric, followed by Jobs / Gross / Cash / Card, the weekly Gross-vs-Net
 * trend, and the detailed THIS WEEK / BREAKDOWN / PERFORMANCE sections retained
 * below for drivers who want more insight.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, RefreshCw, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const gbp = (n) => {
  const v = Number(n ?? 0);
  return v < 0 ? `-£${Math.abs(v).toFixed(2)}` : `£${v.toFixed(2)}`;
};
const hrs = (n) => `${Number(n ?? 0).toFixed(1)}h`;

// Pastel stat card (mobile-first, friendly for drivers).
const PastelCard = ({ label, value, bg, fg, testid }) => (
  <div className="rounded-2xl p-4" style={{ background: bg }} data-testid={testid}>
    <div className="text-xs font-bold uppercase tracking-wide" style={{ color: fg }}>{label}</div>
    <div className="font-heading text-3xl font-black mt-1 text-[#0A0A0A]" data-testid={`${testid}-value`}>{value}</div>
  </div>
);

// Compact white card for the detailed sections underneath.
const MiniStat = ({ label, value, accent = "#0A0A0A", testid }) => (
  <div className="bg-white rounded-xl border border-border p-3" data-testid={testid}>
    <div className="label-mini text-muted-foreground">{label}</div>
    <div className="font-heading text-xl font-black mt-0.5" style={{ color: accent }} data-testid={`${testid}-value`}>{value}</div>
  </div>
);

export default function DriverEarnings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [rating, setRating] = useState(null);
  const [rides, setRides] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login");
    else if (user.role !== "driver") navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setRefreshing(true);
      try {
        const [k, r, m] = await Promise.all([
          api.get("/driver/kpi"),
          api.get("/me/rating"),
          api.get("/driver/my-rides"),
        ]);
        setKpi(k.data || null);
        setRating(r.data || null);
        setRides((m.data || []).filter((x) => x.status === "completed"));
      } catch { /* ignore */ } finally {
        setRefreshing(false);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  if (loading || !user) return null;

  const today = kpi?.today || {};
  const week = kpi?.week || {};
  const breakdown = kpi?.breakdown || {};
  const perf = kpi?.performance || {};
  const history = kpi?.history || [];
  const netToday = Number(today.net ?? 0);

  return (
    <div className="min-h-screen bg-[#FAFAFB]">
      <div className="max-w-xl mx-auto px-4 py-5 space-y-5" data-testid="driver-kpi-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <Link to="/driver" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground" data-testid="earnings-back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" data-testid="kpi-refreshing" />}
        </div>

        <div>
          <div className="label-mini text-muted-foreground">DRIVER · EARNINGS</div>
          <h1 className="font-heading text-3xl font-black tracking-tight mt-0.5">Your earnings</h1>
          <div className="text-xs text-muted-foreground mt-0.5">{kpi?.timezone || "Europe/London"} · week from Monday · live</div>
        </div>

        {!kpi ? (
          <div className="py-16 text-center text-muted-foreground" data-testid="kpi-loading">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading earnings…
          </div>
        ) : (
          <>
            {/* HERO — Today's net earnings (the number drivers look for first) */}
            <div className="rounded-2xl bg-[#0A0A0A] text-white p-6 shadow-lg" data-testid="kpi-hero-net">
              <div className="text-xs font-bold uppercase tracking-widest text-[#FFC107]">Today's net earnings</div>
              <div
                className="font-heading text-5xl sm:text-6xl font-black mt-2 tabular-nums"
                style={{ color: netToday >= 0 ? "#FFFFFF" : "#F87171" }}
                data-testid="kpi-today-net"
              >
                {gbp(netToday)}
              </div>
              <div className="text-sm text-white/60 mt-2">
                Gross {gbp(today.gross)} · {today.jobs ?? 0} job{today.jobs === 1 ? "" : "s"} · after {gbp(breakdown.daily_firm_fee)} firm fee
              </div>
            </div>

            {/* TODAY — Jobs / Gross / Cash / Card */}
            <div className="grid grid-cols-2 gap-3" data-testid="kpi-today">
              <PastelCard label="Jobs" value={today.jobs ?? 0} bg="#DCE9FB" fg="#1E40AF" testid="kpi-today-jobs" />
              <PastelCard label="Gross" value={gbp(today.gross)} bg="#CDEBD7" fg="#065F46" testid="kpi-today-gross" />
              <PastelCard label="Cash" value={gbp(today.cash)} bg="#E5E3FA" fg="#4338CA" testid="kpi-today-cash" />
              <PastelCard label="Card" value={gbp(today.card)} bg="#E5E3FA" fg="#4338CA" testid="kpi-today-card" />
            </div>

            {/* EARNINGS TREND — last 7 days gross vs net */}
            <div className="bg-white rounded-2xl border border-border p-4" data-testid="kpi-history">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#002FA7]" />
                <div className="label-mini text-muted-foreground">EARNINGS TREND · LAST 7 DAYS</div>
              </div>
              {history.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center" data-testid="kpi-history-empty">No earnings history yet.</div>
              ) : (
                <div style={{ width: "100%", height: 240 }} data-testid="kpi-history-chart">
                  <ResponsiveContainer>
                    <BarChart data={history} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => (d || "").slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => gbp(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="gross" name="Gross" fill="#002FA7" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="net" name="Net" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* THIS WEEK */}
            <div data-testid="kpi-week">
              <div className="label-mini text-muted-foreground mb-2">THIS WEEK</div>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="GROSS" value={gbp(week.gross)} testid="kpi-week-gross" />
                <MiniStat label="NET" value={gbp(week.net)} accent={week.net >= 0 ? "#10B981" : "#EF4444"} testid="kpi-week-net" />
                <MiniStat label="JOBS" value={week.jobs ?? 0} accent="#002FA7" testid="kpi-week-jobs" />
                <MiniStat label="HOURS ONLINE" value={hrs(week.hours_online)} testid="kpi-week-hours" />
              </div>
            </div>

            {/* BREAKDOWN */}
            <div data-testid="kpi-breakdown">
              <div className="label-mini text-muted-foreground mb-2">BREAKDOWN · TODAY</div>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="CASH COLLECTED" value={gbp(breakdown.cash_collected)} testid="kpi-breakdown-cash" />
                <MiniStat label="CARD PAYMENTS" value={gbp(breakdown.card_payments)} testid="kpi-breakdown-card" />
                <MiniStat label="DAILY FIRM FEE" value={`-${gbp(breakdown.daily_firm_fee)}`} accent="#EF4444" testid="kpi-breakdown-fee" />
                <MiniStat label="NET AFTER FIRM FEE" value={gbp(breakdown.net_after_firm_fee)} accent={(breakdown.net_after_firm_fee ?? 0) >= 0 ? "#10B981" : "#EF4444"} testid="kpi-breakdown-net" />
              </div>
            </div>

            {/* PERFORMANCE */}
            <div data-testid="kpi-performance">
              <div className="label-mini text-muted-foreground mb-2">PERFORMANCE · THIS WEEK</div>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="AVERAGE FARE" value={gbp(perf.average_fare)} accent="#002FA7" testid="kpi-perf-avgfare" />
                <MiniStat label="EARNINGS / HOUR" value={gbp(perf.earnings_per_hour)} accent="#002FA7" testid="kpi-perf-perhour" />
              </div>
            </div>
          </>
        )}

        {rating?.avg_rating && (
          <div className="bg-white rounded-2xl border border-border p-4 flex items-center gap-3" data-testid="earnings-rating-card">
            <Star className="w-5 h-5 fill-[#FFC107] text-[#FFC107]" />
            <div className="font-bold text-lg">{rating.avg_rating}</div>
            <div className="text-sm text-muted-foreground">({rating.rating_count} ratings)</div>
          </div>
        )}

        {/* Recent completed rides */}
        <div className="bg-white rounded-2xl border border-border p-4">
          <div className="flex items-baseline justify-between mb-3">
            <div className="label-mini text-muted-foreground">RECENT RIDES</div>
            <div className="label-mini text-muted-foreground">{rides.length} TOTAL</div>
          </div>
          {rides.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No completed rides yet.</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="earnings-trip-history">
              {rides.slice(0, 50).map((r) => (
                <div key={r.id} className="border border-border rounded-xl p-3 flex items-center justify-between" data-testid={`earnings-trip-${r.id}`}>
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="text-xs truncate font-semibold">{r.pickup_address} → {r.dropoff_address}</div>
                    <div className="label-mini text-muted-foreground mt-1">
                      {r.passenger_name} ·
                      <span
                        className={`ml-1 font-bold uppercase ${
                          r.payment_status === "paid" ? "text-[#10B981]" :
                          r.payment_status === "invoiced" ? "text-[#0A0A0A]" : "text-[#92400E]"
                        }`}
                        data-testid={`earnings-payment-status-${r.id}`}
                      >
                        {r.payment_status || "pending"}
                      </span>
                    </div>
                  </div>
                  <div className="font-mono-tab font-bold text-sm flex-shrink-0">{gbp(r.fare)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
